import React from "react"

import { CodeBlock } from "@javascript/components/ui/code-block"
import {
  TypographyH2,
  TypographyH3,
  TypographyInlineCode,
  TypographyList,
  TypographyP
} from "@javascript/components/ui/typography"

export function VersioningPage() {
  return (
    <div className="space-y-8">
      <div>
        <TypographyH2>Versioning Reference</TypographyH2>
        <TypographyP>
          Every theme package declares two versions: the theme's own version, and the platform contract version it targets. Understanding the distinction helps you navigate theme compatibility and future updates.
        </TypographyP>
      </div>

      <div>
        <TypographyH3>version: the theme's own version</TypographyH3>
        <TypographyP>
          The <TypographyInlineCode>version</TypographyInlineCode> field in <TypographyInlineCode>theme.json</TypographyInlineCode> is the semantic version of your theme — your choice as the designer.
        </TypographyP>
        <CodeBlock
          code={`{
  "name": "Elegant",
  "version": "1.0.0",
  "contract_version": 3,
  "blocks": [...]
}`}
          language="json"
          className="my-4"
        />
        <TypographyP>
          Increment this when you make design changes, fix bugs, or add features to your theme. This field is for your own versioning discipline and is not validated or enforced by the platform.
        </TypographyP>
      </div>

      <div>
        <TypographyH3>contract_version: the platform contract version</TypographyH3>
        <TypographyP>
          The <TypographyInlineCode>contract_version</TypographyInlineCode> field declares which version of Goldypaper's theme contract your theme targets.
        </TypographyP>
        <TypographyP>
          Contract 3 is the only supported contract. It includes the Liquid and block model plus one optional runtime
          with platform-allowlisted dependencies. Omit <TypographyInlineCode>runtime</TypographyInlineCode> when a theme
          needs no JavaScript.
        </TypographyP>
        <TypographyP>
          When a theme loads, the platform validates that its{" "}
          <TypographyInlineCode>contract_version</TypographyInlineCode>{" "}
          is exactly <TypographyInlineCode>3</TypographyInlineCode>. Any other value is rejected; there is no legacy
          compatibility branch.
        </TypographyP>
        <TypographyP>
          This version covers the interface between your theme and the platform: available presentation blocks and config, Liquid tags, package layout, and the security model. Invitation content is a separate platform-owned schema.
        </TypographyP>
      </div>

      <div>
        <TypographyH3>The distinction</TypographyH3>
        <TypographyP>
          In one sentence: <strong>version is yours, contract_version is ours.</strong>
        </TypographyP>
        <TypographyList>
          <li><TypographyInlineCode>version</TypographyInlineCode> changes when you update your theme's design or features</li>
          <li><TypographyInlineCode>contract_version</TypographyInlineCode> changes when the platform's theme interface changes</li>
        </TypographyList>
        <TypographyP className="text-sm text-muted-foreground">
          Example: you release Elegant v1.0.0 and v1.0.1 with design tweaks. Both declare{" "}
          <TypographyInlineCode>"contract_version": 3</TypographyInlineCode>. Ordinary design version changes do not
          change the platform contract.
        </TypographyP>
      </div>

      <div>
        <TypographyH3>Version pinning is not yet implemented</TypographyH3>
        <TypographyP>
          Today, invitations do not pin to a specific theme version. When a couple publishes an invitation using your theme, that invitation references your theme by key only (e.g., <TypographyInlineCode>"elegant"</TypographyInlineCode>). If you later release a new version, that invitation renders with the new version the next time a guest views it.
        </TypographyP>
        <TypographyP>
          This is a known limitation. In a future release, the platform will support version pinning: an invitation will record which version it was created with, and it will continue to render with that version even after you ship updates. This protects couples from unintended design changes and gives you control over when your users adopt your updates.
        </TypographyP>
        <TypographyP>
          For now, <TypographyInlineCode>version</TypographyInlineCode> is metadata and an asset cache key. The platform
          appends it to theme asset URLs, but does not yet use it to select among stored copies of theme code.
        </TypographyP>
      </div>
    </div>
  )
}
